'use client';
import React, { useState } from 'react';
import { siteConfig } from '../../../../config/site';
import { submitServiceRequest, ServiceRequestPayload } from '@/lib/api';
import { CheckCircleIcon, ExclamationTriangleIcon, PhoneIcon } from '@heroicons/react/24/outline';

const STEPS = [
  'Service Type',
  'Device',
  'Problem',
  'Location & Time',
  'Contact',
  'Review',
];

interface FormState {
  serviceType: 'shop' | 'onsite' | '';
  deviceCategory: string;
  deviceBrand: string;
  deviceModel: string;
  issueType: string;
  description: string;
  address: string;
  apt: string;
  city: string;
  state: string;
  zip: string;
  preferredDate: string;
  timeWindow: string;
  name: string;
  phone: string;
  email: string;
  preferredContact: 'call' | 'whatsapp' | '';
  consent: boolean;
  honeypot: string;
}

const deviceCategories = [
  'iPhone', 'Samsung Phone', 'Other Phone', 'iPad / Tablet',
  'Laptop', 'MacBook', 'Desktop PC',
  'PS5', 'PS4', 'Xbox', 'Nintendo Switch',
  'Other Electronics',
];

const issuesByCategory: Record<string, string[]> = {
  'iPhone': ['Cracked screen', 'Battery drain', 'Charging port', 'Water damage', 'Camera issue', 'Speaker/mic', 'Software issue', 'Other'],
  'Samsung Phone': ['Cracked screen', 'Battery drain', 'Charging port', 'Water damage', 'Camera issue', 'Speaker/mic', 'Other'],
  'Other Phone': ['Cracked screen', 'Battery issue', 'Charging port', 'Other'],
  'iPad / Tablet': ['Cracked screen', 'Battery', 'Charging port', 'Unresponsive screen', 'Other'],
  'Laptop': ['Screen damage', 'Battery', 'Overheating', 'Slow performance', 'No power', 'Keyboard/trackpad', 'Charging port', 'Other'],
  'MacBook': ['Screen damage', 'Battery', 'Overheating', 'No power', 'Keyboard', 'Charging port', 'Slow performance', 'Other'],
  'Desktop PC': ['No power', 'Slow performance', 'Overheating', 'Screen issue', 'Software issue', 'Other'],
  'PS5': ['HDMI port', 'Overheating', 'Random shutdown', 'No power', 'Disc drive', 'Needs cleaning', 'Other'],
  'PS4': ['HDMI port', 'Overheating', 'No power', 'Disc drive', 'Needs cleaning', 'Other'],
  'Xbox': ['HDMI port', 'No power', 'Overheating', 'Disc drive', 'Needs cleaning', 'Other'],
  'Nintendo Switch': ['Cracked screen', 'No power', 'Joy-Con issue', 'Charging port', 'Other'],
  'Other Electronics': ['Not powering on', 'Physical damage', 'Performance issue', 'Other'],
};

export default function RequestServiceForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>({
    serviceType: '',
    deviceCategory: '',
    deviceBrand: '',
    deviceModel: '',
    issueType: '',
    description: '',
    address: '',
    apt: '',
    city: '',
    state: 'New York',
    zip: '',
    preferredDate: '',
    timeWindow: '',
    name: '',
    phone: '',
    email: '',
    preferredContact: '',
    consent: false,
    honeypot: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; notConnected?: boolean; error?: string; referenceNumber?: string } | null>(null);
  const [formStartedAt] = useState(() => Date.now());
  // Idempotency key: generated once per form session, prevents duplicate submissions on retry/refresh
  const [idempotencyKey] = useState(() => {
    const arr = new Uint8Array(16);
    if (typeof window !== 'undefined' && window.crypto) {
      window.crypto.getRandomValues(arr);
    } else {
      for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
  });

  const set = (key: keyof FormState, value: unknown) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateStep = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    if (step === 0) {
      if (!form.serviceType) newErrors.serviceType = 'Please select a service type.';
    }
    if (step === 1) {
      if (!form.deviceCategory) newErrors.deviceCategory = 'Please select a device category.';
    }
    if (step === 2) {
      if (!form.issueType) newErrors.issueType = 'Please select an issue type.';
      if (!form.description.trim()) newErrors.description = 'Please describe the problem.';
    }
    if (step === 3 && form.serviceType === 'onsite') {
      if (!form.address.trim()) newErrors.address = 'Please enter your address.';
      if (!form.city.trim()) newErrors.city = 'Please enter your city.';
      if (!form.zip.trim()) newErrors.zip = 'Please enter your ZIP code.';
    }
    if (step === 4) {
      if (!form.name.trim()) newErrors.name = 'Please enter your name.';
      if (!form.phone.trim()) newErrors.phone = 'Please enter your phone number.';
      else if (!/^\+?[\d\s\-().]{10,}$/.test(form.phone)) newErrors.phone = 'Please enter a valid US phone number.';
      if (!form.preferredContact) newErrors.preferredContact = 'Please select a preferred contact method.';
      if (!form.consent) newErrors.consent = 'Please confirm you agree to be contacted.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    if (form.honeypot) return; // Honeypot check

    setSubmitting(true);
    const payload: ServiceRequestPayload = {
      serviceType: form.serviceType as 'shop' | 'onsite',
      device: { category: form.deviceCategory, brand: form.deviceBrand, model: form.deviceModel },
      problem: { issueType: form.issueType, description: form.description },
      ...(form.serviceType === 'onsite' && {
        location: { address: form.address, apt: form.apt, city: form.city, state: form.state, zip: form.zip },
      }),
      ...(form.preferredDate && { preferredTime: { date: form.preferredDate, window: form.timeWindow } }),
      contact: {
        name: form.name,
        phone: form.phone,
        email: form.email,
        preferredContact: form.preferredContact as 'call' | 'whatsapp',
        consent: form.consent,
      },
      idempotencyKey,
    };
    const res = await submitServiceRequest(payload, formStartedAt);
    setResult({ ...res, referenceNumber: res.data?.referenceNumber });
    setSubmitting(false);
    if (res.success || res.notConnected) setStep(6);
  };

  const inputClass = "w-full px-3.5 py-2.5 rounded-xl text-sm transition-all focus:outline-none";
  const inputStyle = { backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-color)', color: 'var(--text)' };
  const errorStyle = { color: '#ef4444', fontSize: '12px', marginTop: '4px' };

  // Success / error screen
  if (step === 6) {
    if (result?.notConnected) {
      return (
        <div className="rounded-2xl p-8 text-center space-y-5" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)' }}>
          <ExclamationTriangleIcon className="w-10 h-10 mx-auto" style={{ color: '#FBBC05' }} />
          <div>
            <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text)' }}>Online Submission Not Yet Available</h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Online submission is not yet connected. Please call or WhatsApp us directly to arrange your repair — we're happy to help.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={siteConfig.phone.tel}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90"
              style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
            >
              <PhoneIcon className="w-4 h-4" />
              Call {siteConfig.phone.display}
            </a>
            <a
              href={siteConfig.phone.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-2xl p-8 text-center space-y-5" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)' }}>
        <CheckCircleIcon className="w-12 h-12 mx-auto" style={{ color: 'var(--brand)' }} />
        <div>
          <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text)' }}>Request Received!</h2>
          {result?.referenceNumber && (
            <div className="mb-3">
              <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Your ticket number:</p>
              <div className="flex items-center justify-center gap-2">
                <span className="font-mono text-base font-bold px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--surface-2)', color: 'var(--brand)' }}>
                  {result.referenceNumber}
                </span>
                <button
                  onClick={() => navigator.clipboard?.writeText(result.referenceNumber ?? '')}
                  className="text-xs px-2 py-1 rounded border transition-all hover:opacity-80"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}
                >
                  Copy
                </button>
              </div>
            </div>
          )}
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            We'll call you to confirm the details. If you need to reach us sooner:
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={siteConfig.phone.tel}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90"
            style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
          >
            <PhoneIcon className="w-4 h-4" />
            Call Us
          </a>
          <a
            href={siteConfig.phone.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}
          >
            WhatsApp
          </a>
          <a
            href="/track-repair"
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}
          >
            Track Repair
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold" style={{ color: 'var(--brand)' }}>Step {step + 1} of {STEPS.length}</span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{STEPS[step]}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--surface-2)' }}>
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%`, backgroundColor: 'var(--brand)' }}
          />
        </div>
      </div>

      {/* Honeypot */}
      <input
        type="text"
        name="website"
        value={form.honeypot}
        onChange={(e) => set('honeypot', e.target.value)}
        style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
      />

      <div
        className="rounded-2xl p-6"
        style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)' }}
      >
        {/* Step 0: Service Type */}
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="text-base font-bold" style={{ color: 'var(--text)' }}>How would you like service?</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { value: 'shop', title: 'Visit the Shop', desc: 'Bring your device to our Long Island City location.' },
                { value: 'onsite', title: 'On-Site Service', desc: 'We come to your home, office, or company in New York State.' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set('serviceType', opt.value)}
                  className="text-left p-5 rounded-xl border-2 transition-all"
                  style={{
                    backgroundColor: form.serviceType === opt.value ? 'rgba(57,255,20,0.06)' : 'var(--surface-2)',
                    borderColor: form.serviceType === opt.value ? 'var(--brand)' : 'var(--border-color)',
                  }}
                >
                  <div className="font-semibold text-sm mb-1" style={{ color: 'var(--text)' }}>{opt.title}</div>
                  <div className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{opt.desc}</div>
                </button>
              ))}
            </div>
            {errors.serviceType && <p style={errorStyle}>{errors.serviceType}</p>}
          </div>
        )}

        {/* Step 1: Device */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-base font-bold" style={{ color: 'var(--text)' }}>What device needs repair?</h2>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Device Category *</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {deviceCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => { set('deviceCategory', cat); set('issueType', ''); }}
                    className="px-3 py-2 rounded-lg text-xs font-medium border transition-all text-left"
                    style={{
                      backgroundColor: form.deviceCategory === cat ? 'rgba(57,255,20,0.08)' : 'var(--surface-2)',
                      borderColor: form.deviceCategory === cat ? 'var(--brand)' : 'var(--border-color)',
                      color: form.deviceCategory === cat ? 'var(--brand)' : 'var(--text)',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {errors.deviceCategory && <p style={errorStyle}>{errors.deviceCategory}</p>}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Brand (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Apple, Samsung, Sony"
                  value={form.deviceBrand}
                  onChange={(e) => set('deviceBrand', e.target.value)}
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Model (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. iPhone 14, PS5 Disc"
                  value={form.deviceModel}
                  onChange={(e) => set('deviceModel', e.target.value)}
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Problem */}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-base font-bold" style={{ color: 'var(--text)' }}>What's the problem?</h2>
            {form.deviceCategory && issuesByCategory[form.deviceCategory] && (
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Issue Type *</label>
                <select
                  value={form.issueType}
                  onChange={(e) => set('issueType', e.target.value)}
                  className={inputClass}
                  style={inputStyle}
                >
                  <option value="">Select an issue</option>
                  {issuesByCategory[form.deviceCategory].map((issue) => (
                    <option key={issue} value={issue}>{issue}</option>
                  ))}
                </select>
                {errors.issueType && <p style={errorStyle}>{errors.issueType}</p>}
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Describe the problem *</label>
              <textarea
                rows={4}
                placeholder="Describe what's happening with your device in as much detail as possible..."
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                className={`${inputClass} resize-none`}
                style={inputStyle}
              />
              {errors.description && <p style={errorStyle}>{errors.description}</p>}
            </div>
          </div>
        )}

        {/* Step 3: Location & Time */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-base font-bold" style={{ color: 'var(--text)' }}>
              {form.serviceType === 'onsite' ? 'Your Location & Preferred Time' : 'Shop Visit Details'}
            </h2>
            {form.serviceType === 'shop' ? (
              <div
                className="p-4 rounded-xl text-sm space-y-2"
                style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-color)' }}
              >
                <p className="font-semibold" style={{ color: 'var(--text)' }}>Walk in during shop hours:</p>
                <p style={{ color: 'var(--text-muted)' }}>{siteConfig.address.full}</p>
                <p style={{ color: 'var(--text-muted)' }}>Mon–Sat: 11:30 AM – 7:30 PM · Sun: 1:00 PM – 8:00 PM</p>
                <a href={siteConfig.directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-block font-semibold transition-opacity hover:opacity-80" style={{ color: 'var(--brand)' }}>
                  Get Directions →
                </a>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Street Address *</label>
                  <input type="text" placeholder="123 Main St" value={form.address} onChange={(e) => set('address', e.target.value)} className={inputClass} style={inputStyle} />
                  {errors.address && <p style={errorStyle}>{errors.address}</p>}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Apt / Suite (optional)</label>
                    <input type="text" placeholder="Apt 4B" value={form.apt} onChange={(e) => set('apt', e.target.value)} className={inputClass} style={inputStyle} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>City *</label>
                    <input type="text" placeholder="New York" value={form.city} onChange={(e) => set('city', e.target.value)} className={inputClass} style={inputStyle} />
                    {errors.city && <p style={errorStyle}>{errors.city}</p>}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>State</label>
                    <input type="text" value={form.state} onChange={(e) => set('state', e.target.value)} className={inputClass} style={inputStyle} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>ZIP Code *</label>
                    <input type="text" placeholder="10001" value={form.zip} onChange={(e) => set('zip', e.target.value)} className={inputClass} style={inputStyle} />
                    {errors.zip && <p style={errorStyle}>{errors.zip}</p>}
                  </div>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Preferred Date (optional)</label>
                <input type="date" value={form.preferredDate} onChange={(e) => set('preferredDate', e.target.value)} className={inputClass} style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Time Window (optional)</label>
                <select value={form.timeWindow} onChange={(e) => set('timeWindow', e.target.value)} className={inputClass} style={inputStyle}>
                  <option value="">Any time</option>
                  <option value="Morning">Morning</option>
                  <option value="Afternoon">Afternoon</option>
                  <option value="Evening">Evening</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Contact */}
        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-base font-bold" style={{ color: 'var(--text)' }}>Your Contact Information</h2>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Full Name *</label>
              <input type="text" placeholder="Your name" value={form.name} onChange={(e) => set('name', e.target.value)} className={inputClass} style={inputStyle} />
              {errors.name && <p style={errorStyle}>{errors.name}</p>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Phone Number *</label>
                <input type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputClass} style={inputStyle} />
                {errors.phone && <p style={errorStyle}>{errors.phone}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Email (optional)</label>
                <input type="email" placeholder="you@example.com" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass} style={inputStyle} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Preferred Contact Method *</label>
              <div className="flex gap-3">
                {[{ value: 'call', label: 'Phone Call' }, { value: 'whatsapp', label: 'WhatsApp' }].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set('preferredContact', opt.value)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all"
                    style={{
                      backgroundColor: form.preferredContact === opt.value ? 'rgba(57,255,20,0.08)' : 'var(--surface-2)',
                      borderColor: form.preferredContact === opt.value ? 'var(--brand)' : 'var(--border-color)',
                      color: form.preferredContact === opt.value ? 'var(--brand)' : 'var(--text)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {errors.preferredContact && <p style={errorStyle}>{errors.preferredContact}</p>}
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => set('consent', e.target.checked)}
                className="mt-0.5 rounded"
                style={{ accentColor: 'var(--brand)' }}
              />
              <span className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                I agree to be contacted by Forum Technical Solutions by phone or WhatsApp regarding my repair request. *
              </span>
            </label>
            {errors.consent && <p style={errorStyle}>{errors.consent}</p>}
          </div>
        )}

        {/* Step 5: Review */}
        {step === 5 && (
          <div className="space-y-4">
            <h2 className="text-base font-bold" style={{ color: 'var(--text)' }}>Review Your Request</h2>
            <div className="space-y-3 text-sm">
              {[
                { label: 'Service Type', value: form.serviceType === 'shop' ? 'Shop Visit' : 'On-Site Service' },
                { label: 'Device', value: [form.deviceCategory, form.deviceBrand, form.deviceModel].filter(Boolean).join(' · ') },
                { label: 'Issue', value: form.issueType },
                { label: 'Description', value: form.description },
                ...(form.serviceType === 'onsite' ? [{ label: 'Location', value: [form.address, form.apt, form.city, form.state, form.zip].filter(Boolean).join(', ') }] : []),
                { label: 'Name', value: form.name },
                { label: 'Phone', value: form.phone },
                { label: 'Contact via', value: form.preferredContact === 'call' ? 'Phone Call' : 'WhatsApp' },
              ].map((row) => (
                <div key={row.label} className="flex gap-3">
                  <span className="w-28 flex-shrink-0 font-semibold" style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                  <span style={{ color: 'var(--text)' }}>{row.value || '—'}</span>
                </div>
              ))}
            </div>
            <div
              className="p-3 rounded-lg text-xs"
              style={{ backgroundColor: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.15)', color: 'var(--text-muted)' }}
            >
              After submitting, we'll call you to confirm the details.
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        {step > 0 && (
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            disabled={submitting}
            className="flex-1 py-3 rounded-xl text-sm font-semibold border transition-all hover:opacity-80 disabled:opacity-50"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}
          >
            Back
          </button>
        )}
        {step < 5 ? (
          <button
            type="button"
            onClick={handleContinue}
            className="flex-1 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 active:scale-95"
            style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
          >
            Continue
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
            style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        )}
      </div>
    </div>
  );
}